import React, { useState, useEffect, useRef } from 'react';

import Grid from '@mui/material/Grid2';
import { 
  Character,
  CharacterResourceWithUsed,
  CharacterSlot,
  CharacterSheetInterface,
  SpellcastingFeature,
  SpellSlotType,
} from "@dndModels";

// import API from "../../../utilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

// import CharacterResourceBoxes from "../misc/CharacterResourceBoxes";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterSlotInput from "@dndComponents/character/bar3/CharacterSlotInput";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  character: Character; 
  char_interface: CharacterSheetInterface;
  slot_name: string;
  shared: boolean;
}

// const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterSlotsForType({ 
  character,
  char_interface,
  slot_name,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [display_me, setDisplayMe] = useState<CharacterResourceWithUsed | string | null>(null);
  const [spell_slot_types, setSpellSlotTypes] = useState<SpellSlotType[]>([]);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = () => {
    const slots = char_interface.body.use_slots.filter(o => o.slot_name === display_me);
    const slot_types = common_util.unique_array(slots.map(o => o.type_id));
    if (slot_types.length > 0) {
      setLoading(true);
      data_util.getSpellSlotTypes().then(res => {
        setSpellSlotTypes(res);
        setLoading(false);
        setReloading(true);
      });
    } else {
      setReloading(true);
    }
  }

  useEffect(() => {
    if (!fetched) {
      character.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
      load();
      setFetched(true);
    }
    // eslint-disable-next-line
  }, [fetched, setFetched]);

  const resourcesChanged = () => {
    load();
  }

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderSlot = (slot: CharacterSlot) => {
    return (
      <div key={slot.level}>
        <CharacterSlotInput 
          character={character} 
          char_interface={char_interface} 
          slot={slot} 
          shared={shared}        
        />
      </div>
    );
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        onClose={() => { setOpen(false); }}
      >
        { renderDisplayMe() }
      </ModalOrDrawer>
    );
  }

  const toFractionString = (level: number) => {
    if (level === 1) {
      return "Full";
    } else if (level === (1/2)) {
      return "Half";
    } else if (level === (1/3)) {
      return "Third";
    } else if (level === (1/4)) {
      return "Quarter";
    }
    return "";
  }

  const spellLevelNumString = (num: number) => {
    switch (num) {
      case 1: return "First";
      case 2: return "Second";
      case 3: return "Third";
      case 4: return "Fourth";
      case 5: return "Fifth";
      case 6: return "Sixth";
      case 7: return "Seventh";
      case 8: return "Eighth";
      case 9: return "Ninth";
    }
  }

  const renderDisplayMe = () => {
    if (display_me instanceof CharacterResourceWithUsed && display_me.resource_feature) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12} className="header centered">
            Resource: {display_me.name}
          </Grid>
          <Grid size={12}>
            <DisplayStringWithReplacements display_string={display_me.resource_feature.description} cfs={char_interface} />
          </Grid>
        </Grid>
      );
    } else if (typeof display_me === "string") {
      const slots = char_interface.body.use_slots.filter(o => o.slot_name === display_me);
      const slot_types = common_util.unique_array(slots.map(o => o.type_id));
      // console.log(slots);
      // console.log(slot_types);
      return (
        <>
          { slot_types.map(st_id => {
            const slot_type = spell_slot_types.find(o => o._id === st_id);
            if (slot_type) {
              const features_with_type = character.mind.spellcasting_features.filter(o => 
                o.floating_feature instanceof SpellcastingFeature &&
                o.floating_feature.table === slot_type._id
              );
              // console.log(slot_type);
              const feature_details: any[] = [];
              let total_level = 0;
              for (const cf of features_with_type) {
                const sf = cf.floating_feature as SpellcastingFeature;
                const char_class = character.spirit.classes.find(o => o.true_id === cf.source_true_id);
                if (char_class) {
                  const caster_level = sf.level * char_class.level;
                  total_level += caster_level;
                  feature_details.push(
                    <Grid size={12} key={cf.true_id}>
                      { char_class.name }: { toFractionString(sf.level) } Caster ({caster_level})
                    </Grid>
                  );
                }
              }
              const entry = slot_type.entries.sort((a,b) => b.level - a.level).find(o => o.level <= total_level);
              // console.log(entry);
              if (entry) {
                let entry_details = "You have:\r\n";
                const levels = Object.keys(entry.slots_per_level).map(o => +o);
                for (let i = 0; i < levels.length; ++i) {
                  const level = levels[i];
                  const slots_for_level = entry.slots_per_level[level];
                  if (entry_details.length > 14) {
                    entry_details += ",\r\n";
                    if (i === levels.length - 1) {
                      entry_details += "and ";
                    }
                  }
                  entry_details += `${slots_for_level} ${ spellLevelNumString(level) } Level Slot${slots_for_level === 1 ? "" : "s"}`;
                }
                entry_details += ".";
                return (
                  <Grid container spacing={0} direction="row" key={slot_type._id}>
                    <Grid size={12} className="header centered">
                      Slot: {slot_type.name}
                    </Grid>
                    { feature_details }
                    { feature_details.length > 1 && 
                      <Grid size={12}>
                        Total Caster Level: { total_level }
                      </Grid>
                    }
                    <Grid size={12}>&nbsp;</Grid>
                    <Grid size={12}>
                      <DisplayStringWithReplacements display_string={ entry_details } />
                    </Grid>
                    <Grid size={12}>&nbsp;</Grid>
                    <Grid size={12}>
                      These refresh when you take a { slot_type.refresh_rule }.
                    </Grid>
                  </Grid>
                );
              }
            } 
            return null;
          })}
        </>
      );
    }
    return <></>;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const slots = char_interface.body.use_slots.filter(o => o.slot_name === slot_name);
    const groups: any = [];
    let group: any = [];
    // debugger;
    // console.log(slots);
    for (let i = 0; i < slots.length; ++i) {
      let xs = 12;
      if (slots.length > 3) {
        if (slots.length < 7) {
          if (i < 3) {
            xs = 7;
          } else {
            xs = 5;
          }
        } else {
          if (i < 3) {
            xs = 5;
          } else if (i < 6) {
            xs = 4;
          } else {
            xs = 3;
          }
        }
      }
      const slot = slots[i];
      group.push(renderSlot(slot));
      if (i % 3 === 2 || i === slots.length - 1) {
        groups.push(
          <Grid
            container
            spacing={0}
            direction="column"
            key={i}
            size={{
              xs
            }}>
            { group }
          </Grid>
        );
        group = [];
      }
    }
    const width = (slots.length === 1 ? "140px" : (slots.length < 4 ? "153px" : (slots.length < 7 ? "245px" : "320px")));
    return (
      <span key={slot_name} style={{ 
        borderRight: "1px solid",
        width
      }}>
        <Grid container spacing={0} direction="row" className="padded" style={{ width }}>
          <Grid size={12} className="header centered clickable"
            onClick={() => {
              setDisplayMe(slot_name);
              setOpen(true);
            }}>
            { slot_name }
          </Grid>
          { groups }
        </Grid>
        { renderExtras() }
      </span>
    );
  }
}
